import { onBeforeUnmount, onMounted, ref } from "vue";
import { useConvexAuthToken } from "@/app/composables/useConvexAuthToken";
import { getConvexAuthInfo } from "@/v2Projects/convexClient";

/**
 * Exposes the acting expert organization id from the Laravel-issued Convex JWT
 * as a reactive ref. Prefer the already-configured Convex client when available,
 * but fetch/decode the JWT directly as a fallback so wallet queries do not depend
 * on the projects feature flag booting Convex.
 */
export function useCurrentExpertOrgId() {
  const orgId = ref<number | null>(getConvexAuthInfo()?.orgId ?? null);
  const { fetchConvexToken } = useConvexAuthToken();

  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let stopTimer: ReturnType<typeof setTimeout> | null = null;
  let syncPromise: Promise<void> | null = null;

  function decodeJwtPayload(token: string): Record<string, unknown> | null {
    try {
      const [, payloadSegment] = token.split(".");
      if (!payloadSegment) return null;
      const base64 = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
      const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
      return JSON.parse(atob(padded)) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  function orgIdFromToken(token: string | null): number | null {
    if (!token) return null;

    const payload = decodeJwtPayload(token);
    const rawOrgId = payload?.org_id;

    if (typeof rawOrgId === "number" && Number.isFinite(rawOrgId) && rawOrgId > 0) {
      return rawOrgId;
    }

    if (typeof rawOrgId === "string") {
      const parsed = Number(rawOrgId);
      if (Number.isInteger(parsed) && parsed > 0) {
        return parsed;
      }
    }

    return null;
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    if (stopTimer) {
      clearTimeout(stopTimer);
      stopTimer = null;
    }
  }

  function setNextOrgId(next: number | null) {
    if (next !== orgId.value) {
      orgId.value = next;
    }
    if (orgId.value) {
      stopPolling();
    }
  }

  async function sync() {
    const convexOrgId = getConvexAuthInfo()?.orgId ?? null;
    if (convexOrgId) {
      setNextOrgId(convexOrgId);
      return;
    }

    if (syncPromise) {
      return syncPromise;
    }

    syncPromise = fetchConvexToken(false)
      .then((token) => {
        setNextOrgId(orgIdFromToken(token));
      })
      .finally(() => {
        syncPromise = null;
      });

    return syncPromise;
  }

  function syncInBackground() {
    void sync();
  }

  onMounted(() => {
    syncInBackground();
    if (!orgId.value) {
      pollTimer = setInterval(syncInBackground, 1000);
      // Give up the polling fallback after a while; focus/visibility still re-sync.
      stopTimer = setTimeout(stopPolling, 30_000);
    }
    window.addEventListener("focus", syncInBackground);
    document.addEventListener("visibilitychange", syncInBackground);
  });

  onBeforeUnmount(() => {
    stopPolling();
    window.removeEventListener("focus", syncInBackground);
    document.removeEventListener("visibilitychange", syncInBackground);
  });

  return { orgId };
}
